import React, {useMemo} from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';

import {Menu} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Title} from 'src/design/atoms/Title';
import fileDownload from 'js-file-download';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {ItemAssignmentSimpleResponse} from 'src/api/ProactApiClient';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {useExportItemStatusReportMutation} from 'src/redux/queries/itemDefinition';
import {ExportButton} from 'src/design/molecules/ExportButton';
import {Pill} from 'src/design/atoms/Pill';
import {ItemAssignmentStateDataOptions} from 'src/shared/constants/columnFilterData';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {useGetItemAssignmentListV2Query} from 'src/redux/queries/itemAssignment';
import {ItemAssignmentPillConfig} from './itemAssignmentHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface ItemAssignmentsTableByUserProps {
  assignedToUserId: number;
  assignedToUserLabel: string;
}

export const ItemAssignmentsTableByUser: React.FC<
  ItemAssignmentsTableByUserProps
> = ({assignedToUserId, assignedToUserLabel}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const [triggerExportItemStatusReport] = useExportItemStatusReportMutation();

  const columns = useMemo<MRT_ColumnDef<ItemAssignmentSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'assignedByUser.name',
        header: 'Assigned By',
      },
      {
        accessorKey: 'internalIdentifier',
        header: 'Identifier',
      },
      {
        accessorKey: 'state',
        header: 'Status',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: ItemAssignmentStateDataOptions,
        },
        Cell: ({cell}) => (
          <>
            {ItemAssignmentPillConfig(cell.row.original).map(pill => (
              <Pill {...pill} />
            ))}
          </>
        ),
      },
      {
        accessorKey: 'serial',
        header: 'Serial',
        maxSize: 150,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        Cell: ({cell}) => (
          <>{cell.getValue<string | undefined>()?.abbreviate(50)}</>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created On',
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
      {
        accessorKey: 'assignedAt',
        header: 'Assigned On',
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
      {
        accessorKey: 'expiresAt',
        header: 'Expires On',
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
      {
        accessorKey: 'closedAt',
        header: 'Closed On',
        Cell: ({cell}) => (
          <>{cell.getValue<Date | undefined>()?.toString().toShortDate()}</>
        ),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'item-assignment-v2-user',
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'createdAt', desc: true}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isFetching, isError, refetch} =
    useGetItemAssignmentListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
        assignedToUserId: Number(assignedToUserId),
      },
      {skip: !assignedToUserId}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleNavigateToAssignment = (row: ItemAssignmentSimpleResponse) =>
    navigate(PathUtility.ItemAssignmentView(row.itemDefinitionId, row.id));

  const handleNavigateToCertificate = (row: ItemAssignmentSimpleResponse) =>
    navigate(
      PathUtility.ItemTrackerIssueCertificate(row.itemDefinitionId, row.id)
    );

  const handleNavigateToClone = (row: ItemAssignmentSimpleResponse) =>
    navigate(PathUtility.ItemAssignmentClone(row.itemDefinitionId, row.id));

  const exportAssignment = async () => {
    try {
      const response = await triggerExportItemStatusReport({
        userId: assignedToUserId,
      }).unwrap();

      fileDownload(
        response!.data,
        `item_for_${assignedToUserLabel}_export.csv`
      );
      setAlert('Your file has been successfully exported. ', 'success');
    } catch {
      setAlert('Failed to export item assignments.', 'error');
    }
  };

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActionMenuItems: ({row}) => (
        <>
          <Menu.Item onClick={() => handleNavigateToAssignment(row.original)}>
            View
          </Menu.Item>
          {row.original.hasIssueCertificate && (
            <Menu.Item
              onClick={() => handleNavigateToCertificate(row.original)}>
              View Certificate
            </Menu.Item>
          )}

          <PermissionGuard
            module={ModuleEnum.ItemTrackerAssignments}
            operation={PermissionOperationEnum.Create}>
            <Menu.Item onClick={() => handleNavigateToClone(row.original)}>
              Clone
            </Menu.Item>
          </PermissionGuard>
        </>
      ),
      renderTopToolbarCustomActions: () => (
        <Group justify="space-between" gap={3}>
          <PermissionGuard
            module={ModuleEnum.ItemTrackerAssignments}
            operation={PermissionOperationEnum.Export}>
            <ExportButton
              aria-label="exportAssignmentBtnUI"
              onClick={() => exportAssignment()}
            />
          </PermissionGuard>
        </Group>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <Title pb="lg">Item Assignments</Title>
      {table}
    </ContentWrapper>
  );
};
